import React, { useState, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    VStack,
    HStack,
    Text,
    Input,
    FormControl,
    FormLabel,
    Select,
    Alert,
    AlertIcon,
    Progress,
    useToast,
    Tooltip,
    Icon,
    Center,
    Divider
} from '@chakra-ui/react';
import { MdUpload, MdDescription, MdInfo } from 'react-icons/md';
import { useDocuments } from './hooks/useDocuments';
import { useOcrTierOptions } from './hooks/useOptions';

const DocumentUpload = ({ onUploadComplete }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [selectedTier, setSelectedTier] = useState('standard');
    const { uploadDocument, isLoading } = useDocuments();
    const fileInputRef = useRef(null);
    const { options: ocrTierOptions, isLoading: tiersLoading } = useOcrTierOptions();
    const toast = useToast();

    // File validation
    const validateFile = useCallback((file) => {
        if (!file) return { valid: false, message: 'No file selected' };

        if (file.type !== 'application/pdf') {
            return { valid: false, message: 'Only PDF files are supported' };
        }

        if (file.size > 50 * 1024 * 1024) { // 50MB
            return { valid: false, message: 'File size must be less than 50MB' };
        }

        return { valid: true };
    }, []);

    // Handle file selection
    const handleFileSelect = useCallback((file) => {
        const validation = validateFile(file);
        if (validation.valid) {
            setSelectedFile(file);
        } else {
            toast({
                title: 'Invalid File',
                description: validation.message,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        }
    }, [validateFile, toast]);

    // File input change
    const handleFileInputChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    }, [handleFileSelect]);

    // Drag and drop handlers
    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files[0]) {
            handleFileSelect(files[0]);
        }
    }, [handleFileSelect]);

    // Upload handler
    const handleUpload = useCallback(async () => {
        if (!selectedFile) return;

        try {
            await uploadDocument(selectedFile, selectedTier);

            // Clear selection
            setSelectedFile(null);
            setSelectedTier('standard');

            // Notify parent
            if (onUploadComplete) {
                onUploadComplete();
            }

        } catch (error) {
            // Error is handled by the hook
            console.error('Upload failed:', error);
        }
    }, [selectedFile, selectedTier, uploadDocument, onUploadComplete]);

    // Clear selection
    const handleClear = useCallback(() => {
        setSelectedFile(null);
        setSelectedTier('standard');
    }, []);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    return (
        <Box
            bg="white"
            borderRadius="lg"
            border="2px dashed"
            borderColor={dragActive ? 'brand.500' : 'gray.300'}
            p={6}
            transition="border-color 0.2s"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
        >
            <VStack spacing={4}>
                {/* Upload Area */}
                {!selectedFile ? (
                    <Center
                        minH="120px"
                        w="full"
                        bg={dragActive ? 'brand.50' : 'gray.50'}
                        borderRadius="md"
                        transition="background-color 0.2s"
                        position="relative"
                    >
                        <VStack spacing={3}>
                            <Icon
                                as={MdUpload}
                                boxSize={8}
                                color={dragActive ? 'brand.500' : 'gray.400'}
                            />
                            <VStack spacing={1}>
                                <Text fontWeight="500" color="gray.700">
                                    Drop your PDF here or click to browse
                                </Text>
                                <Text fontSize="sm" color="gray.500">
                                    Maximum size: 50MB
                                </Text>
                            </VStack>
                            <Input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={handleFileInputChange}
                                position="absolute"
                                top={0}
                                left={0}
                                opacity={0}
                                width="100%"
                                height="100%"
                                cursor="pointer"
                            />
                        </VStack>
                    </Center>
                ) : (
                    /* Selected File Display */
                    <Box w="full">
                        <Alert status="info" borderRadius="md">
                            <AlertIcon as={MdDescription} />
                            <VStack align="flex-start" spacing={1} flex="1">
                                <HStack justify="space-between" w="full">
                                    <Text fontWeight="500" isTruncated>
                                        {selectedFile.name}
                                    </Text>
                                    <Text fontSize="sm" color="gray.600">
                                        {formatFileSize(selectedFile.size)}
                                    </Text>
                                </HStack>
                                <Text fontSize="sm" color="gray.600">
                                    Ready for upload
                                </Text>
                            </VStack>
                        </Alert>
                    </Box>
                )}

                {/* OCR Settings */}
                <Box w="full">
                    <Divider mb={4} />
                    <FormControl>
                        <VStack align="flex-start" spacing={3}>
                            <HStack>
                                <FormLabel htmlFor="ocr-tier" mb="0" fontWeight="500">
                                    OCR Processing Tier
                                </FormLabel>
                                <Tooltip
                                    label="Choose the processing tier based on your document complexity and quality requirements"
                                    hasArrow
                                >
                                    <Icon as={MdInfo} color="gray.400" />
                                </Tooltip>
                            </HStack>
                            
                            {tiersLoading ? (
                                <Alert status="info" size="sm" borderRadius="md">
                                    <AlertIcon />
                                    Loading available tiers...
                                </Alert>
                            ) : (
                                <Select
                                    id="ocr-tier"
                                    value={selectedTier}
                                    onChange={(e) => setSelectedTier(e.target.value)}
                                    bg="white"
                                    size="md"
                                >
                                    {ocrTierOptions.map((tier) => (
                                        <option key={tier.value} value={tier.value}>
                                            {tier.label}
                                        </option>
                                    ))}
                                </Select>
                            )}
                            
                            {/* Show description for selected tier */}
                            {selectedTier && ocrTierOptions.length > 0 && (
                                <Text fontSize="sm" color="gray.600" bg="gray.50" p={2} borderRadius="md">
                                    {ocrTierOptions.find(tier => tier.value === selectedTier)?.description}
                                </Text>
                            )}
                        </VStack>
                    </FormControl>
                </Box>

                {/* Action Buttons */}
                <HStack spacing={3} w="full" justify={selectedFile ? "space-between" : "center"}>
                    {selectedFile ? (
                        <>
                            <Button
                                variant="ghost"
                                onClick={handleClear}
                                isDisabled={isLoading}
                            >
                                Clear
                            </Button>
                            <Button
                                colorScheme="brand"
                                onClick={handleUpload}
                                isLoading={isLoading}
                                loadingText="Uploading..."
                                leftIcon={<MdUpload />}
                            >
                                Upload & Process
                            </Button>
                        </>
                    ) : (
                            <Button
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Browse Files
                            </Button>
                    )}
                </HStack>

                {/* Processing Info */}
                {isLoading && (
                    <Box w="full">
                        <Progress size="sm" isIndeterminate colorScheme="brand" borderRadius="md" />
                        <Text fontSize="xs" color="gray.500" mt={2} textAlign="center">
                            Uploading and starting OCR processing...
                        </Text>
                    </Box>
                )}
            </VStack>
        </Box>
    );
};

export default DocumentUpload;